import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import { useGqlQuery } from '@api/gql/gqlServices'
import {
  GetClaimablesDocument,
  GetClaimablesQuery,
  ClaimableItemType,
  useMarkClaimableItemAsUsedMutation,
  AddOnChainTransactionHistoryMutation,
  MarkClaimableItemAsUsedMutation,
  ClaimableItem
} from '@generated/generates'
import { useAddOnchainHistoryLog } from '@hooks'

interface ReturnType {
  isLoading: boolean
  claimablesItemsStatus: 'error' | 'success' | 'pending'
  markAsClaimed: (
    id: string,
    tx: { transactionHash: string; to: string }
  ) => Promise<[AddOnChainTransactionHistoryMutation, MarkClaimableItemAsUsedMutation]>
  claimableItems: ClaimableItem[] | undefined | null
}

const useClaim = (type: ClaimableItemType): ReturnType => {
  const queryClient = useQueryClient()
  const { addOnchainHistoryLog } = useAddOnchainHistoryLog()
  const {
    data: claimablesData,
    isPending: isClaimableItemsLoading,
    status: claimablesItemsStatus
  } = useGqlQuery<GetClaimablesQuery>(GetClaimablesDocument)

  const { mutateAsync: markAsClaimedMutation, isPending: isMarkClaimablesLoading } = useMarkClaimableItemAsUsedMutation(
    graphqlClient(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['GetUser'] })
        queryClient.invalidateQueries({ queryKey: ['GetClaimables'] })
        queryClient.invalidateQueries({ queryKey: ['GetInventoryItems'] })
        queryClient.invalidateQueries({ queryKey: ['GetInventoryItemsByIds'] })
        queryClient.invalidateQueries({ queryKey: ['inventoryItemsWithMetadata'] })
      }
    }
  )

  const markAsClaimed = async (id: string, tx: { transactionHash: string; to: string }) => {
    return Promise.all([
      addOnchainHistoryLog({
        txAddress: tx.transactionHash,
        contractAddress: tx.to,
        description: 'Item claimed'
      }),
      markAsClaimedMutation({ markClaimableItemAsUsedId: id })
    ])
  }

  const isLoading = isClaimableItemsLoading || isMarkClaimablesLoading

  const claimableItems =
    claimablesItemsStatus === 'success'
      ? (claimablesData?.viewer?.claimableItems.filter((item) => item.type === type) as ClaimableItem[])
      : null

  return {
    isLoading,
    claimablesItemsStatus,
    markAsClaimed,
    claimableItems
  }
}

export default useClaim
